import { HttpClient, HttpParams } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import { Injectable } from "@angular/core";
import { environment } from "../config/env";
import { Helper } from "../shared/helper";

export interface ResponseModel<T> {
  success: boolean;
  code: string;
  data: T;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  is_loading = false;
  private API_URL = environment.API_URL;

  constructor(
    private http: HttpClient,
    public helper: Helper,
  ) {}

  async post<T, R>({
    url,
    parameters,
  }: {
    url: string;
    parameters?: T;
  }): Promise<ResponseModel<R>> {
    this.setLoading(true);

    try {
      const BASE_URL = this.getBaseUrl(url);
      const call_url = BASE_URL + url;

      const responseData: any = await lastValueFrom(
        this.http.post(call_url, parameters),
      );
      
      return this.normalizeResponse<R>(responseData);
    } catch (error) {
      return { success: false, code: "2003", data: null };
    } finally {
      this.setLoading(false);
    }
  }

  async get<R>({ url }: { url: string }): Promise<ResponseModel<R>> {
    this.setLoading(true);

    try {
      const call_url = this.API_URL + url;
      const responseData: any = await lastValueFrom(this.http.get(call_url));

      if (!responseData) {
        return { success: true, code: "", data: null };
      }

      if (responseData.success) {
        return {
          success: true,
          code: responseData.message,
          data: responseData as R,
        };
      }

      if (url === "/get_language_list") {
        return { success: null, code: null, data: null };
      }

      return {
        success: false,
        code: responseData.error_code,
        data: null,
      };
    } catch {
      return { success: false, code: "2003", data: null };
    } finally {
      this.setLoading(false);
    }
  }

  async getwithparams<R>({
    url,
    params,
  }: {
    url: string;
    params: HttpParams | Record<string, any>;
  }): Promise<ResponseModel<R>> {
    this.setLoading(true);

    try {
      const BASE_URL = this.getBaseUrl(url);
      const call_url = BASE_URL + url;

      const responseData: any = await lastValueFrom(
        this.http.get(call_url, { params: params as any }),
      );

      return this.normalizeResponse<R>(responseData);
    } catch {
      return { success: false, code: "2003", data: null };
    } finally {
      this.setLoading(false);
    }
  }

  private normalizeResponse<R>(responseData: any): ResponseModel<R> {
    // No response at all → treat as success with null data
    if (!responseData) {
      return { success: true, code: "", data: null };
    }

    // Explicit success = false
    if (responseData.success === false) {
      return {
        success: false,
        code:
          responseData.error_code || responseData.message || "UNKNOWN_ERROR",
        data: null,
      };
    }

    // Explicit success = true
    if (responseData.success === true) {
      return {
        success: true,
        code: responseData.message || "",
        data: responseData.data ?? (responseData as R),
      };
    }

    // 🚨 success field missing → infer
    const looksLikeError =
      responseData.error_code || responseData.errors || responseData.error;

    if (looksLikeError) {
      return {
        success: false,
        code:
          responseData.error_code || responseData.message || "UNKNOWN_ERROR",
        data: null,
      };
    }

    // ✅ Assume success
    return {
      success: true,
      code: responseData.message || "",
      data: responseData.data ?? (responseData as R),
    };
  }

  private setLoading(state: boolean) {
    if (state) {
      this.is_loading = true;
    } else {
      setTimeout(() => (this.is_loading = false), 300);
    }
  }

  private getBaseUrl(url: string): string {
    return url.split("/")[1] === "history"
      ? environment.HISTORY_API_URL
      : environment.API_URL;
  }
}
